import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import useChatEvents from '../hooks/useChatEvents';
import FolderTreeDiff from './FolderTreeDiff';
import ArcOrb, { useArcOrbState } from './ArcOrb';
import SchemePicker from './SchemePicker';

const PLAN_TIMEOUT_MS = 180_000;
const APPLY_TIMEOUT_MS = 240_000;
const ACTIVITY_TIMEOUT_MS = 30_000;

const TOOL_LABELS = {
  categorize_documents: 'Categorizing documents',
  refine_folder_plan: 'Refining folder plan',
  apply_folder_plan: 'Applying folder structure',
  list_folder_tree: 'Reading current folders',
  delete_folder: 'Deleting folder',
  create_folder: 'Creating folder',
  rename_folder: 'Renaming folder',
  set_document_folder: 'Moving documents',
  list_documents: 'Listing documents',
};
function toolLabel(name) {
  return TOOL_LABELS[name] || name?.replace(/_/g, ' ') || 'working';
}

function flattenTree(nodes, out = []) {
  for (const n of nodes) {
    out.push(n);
    if (n.children?.length) flattenTree(n.children, out);
  }
  return out;
}

/**
 * Compact signature of the folder tree: sorted "path:doc_count" pairs.
 * Used to verify that an apply turn actually mutated state when the
 * apply_complete event is missed/lost in the agent event stream.
 */
function treeSignature(treeData) {
  if (!treeData) return '';
  const nodes = treeData.tree || [];
  const flat = flattenTree(nodes);
  const parts = flat
    .map(n => `${n.path || ''}:${n.doc_count || 0}`)
    .sort();
  return `u=${treeData.unfiled_count || 0}|t=${treeData.total_count || 0}|${parts.join('|')}`;
}

// documentCounts: Map(folder path → documents in it) — Reorganize files documents, not the files
// they were cut from, so its numbers are the Documents unit's (null = the server tree's).
// filesThatStay: Map(folder path → files in it that Reorganize never moves) — such a folder is
// neither emptied nor removed, whatever the plan does with its documents.
// handPlaced / copies: Map(folder path → documents a person filed there / second appearances) —
// a plan leaves those too (hand-filed ones unless "Move those too" is ticked).
export default function ReorganizeDrawer({
  caseId, currentTree, documentCounts = null, filesThatStay = null, handPlaced = null, copies = null, onClose, onApplied,
}) {
  const { token } = useAuth();

  const [phase, setPhase] = useState('idle');
  const { orbVisible, orbState, show: showOrb, triggerDone: orbDone, triggerError: orbError, hide: hideOrb } = useArcOrbState();
  // Drive orb state from phase transitions
  useEffect(() => {
    if (phase === 'starting' || phase === 'running' || phase === 'applying') showOrb();
    else if (phase === 'ready' || phase === 'done') orbDone();
    else if (phase === 'error') orbError();
    else hideOrb();
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps
  const [conversationId, setConversationId] = useState(null);
  const [pendingPlan, setPendingPlan] = useState(null);
  const [mode, setMode] = useState('standard');  // 'standard' | 'custom'
  const [customInstructions, setCustomInstructions] = useState('');
  // Empty default — SchemePicker auto-selects the recommended scheme on
  // load. If no scheme is recommended (e.g. brand-new case with no
  // classified docs), the Preview button stays disabled until the user
  // picks one deliberately.
  const [strategy, setStrategy] = useState('');
  const [scope, setScope] = useState('all');
  const [excludeFolders, setExcludeFolders] = useState(new Set());
  const [sourceFolder, setSourceFolder] = useState('');
  const [deleteEmptyFolders, setDeleteEmptyFolders] = useState(true);
  // What a person filed by hand stays where it is unless they ask for those to be moved too.
  const [includeHandPlaced, setIncludeHandPlaced] = useState(false);
  const [refinement, setRefinement] = useState('');
  const [iterating, setIterating] = useState(false);
  const [error, setError] = useState(null);
  const [progressSteps, setProgressSteps] = useState([]);
  const [streamText, setStreamText] = useState('');
  const [critique, setCritique] = useState(null);
  // Verification + termination contract signals — non-fatal warnings surfaced
  // after a turn completes. apply_verified compares requested vs actual state;
  // termination_check flags chat-path final messages that violated the
  // DONE/BLOCKED/BUDGET contract (forbidden "I will continue…" prose, etc.).
  const [verification, setVerification] = useState(null);
  const [terminationWarning, setTerminationWarning] = useState(null);
  // Pass 1: explain "no automatic placements" no-op plans + offer one-click
  // merges of duplicate-purpose folders (e.g. /Other vs /Other Documents).
  const [classifySummary, setClassifySummary] = useState(null);
  const [mergeSuggestion, setMergeSuggestion] = useState(null);

  // plan_error / budget_exceeded structured error (object, not string)
  const [planError, setPlanError] = useState(null);
  // Heartbeat status: {tool, elapsed_ms} — shown in spinner region
  const [heartbeatStatus, setHeartbeatStatus] = useState(null);
  // Phase 3.2: dry-run preview data shown between scheme pick and propose
  // when the lawyer-first flag is on. null = no preview fetched yet.
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  // Activity watchdog: fires when no heartbeat/tool activity for 30s
  const [activityStalled, setActivityStalled] = useState(false);
  // Metadata from plan_ready event: {strategy, source_folder, folder_count, total_documents}
  // Used to render a scope-correct plan summary (redistribute shows moved-docs count,
  // not the full-case total_count that the pending plan tree carries).
  const [planMeta, setPlanMeta] = useState(null);
  const selfFiling = planMeta?.folders_with_rules || 0;   // folders that will file new documents by themselves

  const refinementRef = useRef(null);
  const progressEndRef = useRef(null);
  const progressRef = useRef([]);
  const streamTextRef = useRef('');
  const seenSeqsRef = useRef(new Set());
  const phaseRef = useRef(phase);
  const conversationIdRef = useRef(null);
  const watchdogRef = useRef(null);
  const deadlineRef = useRef(0);
  const activityWatchdogRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  // Persists across poll batches: true once categorize/refine tool_result seen this turn
  const planToolRanRef = useRef(false);
  const applyToolRanRef = useRef(false);
  // Set when a plan-producing tool returns an error (e.g. redistribute BLOCKED
  // because no docs match). Routes the turn-done branch to a terminal "blocked"
  // state that keeps the agent's BLOCKED message visible instead of spinning.
  const planToolErroredRef = useRef(false);
  // Folder-tree signature captured right before approval is sent.
  // Used as a state-of-the-world fallback when the agent's apply_complete
  // event is split across poll batches and the watchdog wrongly fires.
  const applyStartSignatureRef = useRef(null);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearInterval(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  const startWatchdog = useCallback((timeoutMs, message) => {
    stopWatchdog();
    deadlineRef.current = Date.now() + timeoutMs;
    watchdogRef.current = setInterval(() => {
      if (Date.now() > deadlineRef.current) {
        stopWatchdog();
        setError(message);
        setPhase('error');
      }
    }, 3000);
  }, [stopWatchdog]);

  const stopActivityWatchdog = useCallback(() => {
    if (activityWatchdogRef.current) {
      clearInterval(activityWatchdogRef.current);
      activityWatchdogRef.current = null;
    }
    setActivityStalled(false);
    setHeartbeatStatus(null);
  }, []);

  const startActivityWatchdog = useCallback(() => {
    stopActivityWatchdog();
    lastActivityRef.current = Date.now();
    activityWatchdogRef.current = setInterval(() => {
      if (Date.now() - lastActivityRef.current > ACTIVITY_TIMEOUT_MS) {
        setActivityStalled(true);
      }
    }, 5000);
  }, [stopActivityWatchdog]);

  // ── Event handler — drives all phase transitions from agent chatter ──
  const handleEvents = useCallback((events) => {
    let planReadyEvent = false;      // skill executor emitted "plan_ready"
    let categorizeFinished = false;  // fallback for chat-path reorganize
    let applyFinished = false;
    let turnDone = false;
    let foundError = null;

    for (const evt of events) {
      if (evt.sequence != null) {
        if (seenSeqsRef.current.has(evt.sequence)) continue;
        seenSeqsRef.current.add(evt.sequence);
      }
      const t = evt.event_type || evt.type;
      const d = evt.data ?? evt;

      if (t === 'tool_call' || t === 'tool_result' || t === 'streaming_text') {
        // Reset activity watchdog on any sign of life
        lastActivityRef.current = Date.now();
        setActivityStalled(false);
      }

      if (t === 'agent_heartbeat') {
        lastActivityRef.current = Date.now();
        setActivityStalled(false);
        if (d.current_tool) {
          setHeartbeatStatus({ tool: d.current_tool, elapsed_ms: d.elapsed_ms || 0 });
        }
      } else if (t === 'plan_error') {
        stopWatchdog();
        stopActivityWatchdog();
        setPlanError({
          message: d.message || 'Arc encountered an error generating the plan.',
          hint: d.hint || '',
          tool: d.tool || '',
          phase: d.phase || 'generate',
          recoverable: d.recoverable !== false,
          partial_plan: d.partial_plan || null,
        });
        setPhase('error');
        return;
      } else if (t === 'plan_budget_exceeded') {
        stopWatchdog();
        stopActivityWatchdog();
        const copy = d.dimension === 'wall_clock'
          ? `This took longer than expected (${Math.round((d.actual || 0) / 1000)}s limit: ${Math.round((d.limit || 0) / 1000)}s).`
          : `This used more AI budget than expected ($${(d.actual || 0).toFixed(3)} / $${(d.limit || 0).toFixed(3)}).`;
        setPlanError({
          message: copy,
          hint: 'Try a simpler scope, or reduce the number of documents.',
          tool: d.last_step || '',
          phase: 'generate',
          recoverable: true,
        });
        setPhase('error');
        return;
      }

      if (t === 'tool_call') {
        progressRef.current.push({
          kind: 'started',
          text: `${toolLabel(d.tool_name ?? d.name ?? d.tool)}…`,
        });
      } else if (t === 'tool_result') {
        const name = d.tool_name ?? d.name ?? d.tool;
        const ms = d.duration_ms ? ` (${d.duration_ms}ms)` : '';
        progressRef.current.push({
          kind: 'done',
          text: `${toolLabel(name)}${ms}`,
        });
        // Chat-path fallback: any plan-producing tool signals plan-ready
        if (
          name === 'categorize_documents' ||
          name === 'refine_folder_plan' ||
          name === 'redistribute_folder'
        ) {
          // Backend tool_result events carry status='error' when the tool
          // returned an error payload (e.g. redistribute BLOCKED on docs
          // that can't match any peer). In that case there's no plan to
          // fetch — flag it so turnDone routes to a terminal blocked state.
          if (d.status === 'error' || d.error) {
            planToolErroredRef.current = true;
          } else {
            categorizeFinished = true;
            planToolRanRef.current = true;
          }
        }
        if (name === 'apply_folder_plan') {
          applyFinished = true;
          applyToolRanRef.current = true;
        }
        // Pass 1: capture LLM placement reasoning + duplicate-folder hint
        // for the no-op-plan callout and the "Merge them" button.
        if (name === 'classify_into_existing_folders' && d.classify_summary) {
          setClassifySummary(d.classify_summary);
        }
        if (name === 'redistribute_folder' && d.merge_suggestion) {
          setMergeSuggestion(d.merge_suggestion);
        }
      } else if (t === 'streaming_text') {
        streamTextRef.current += d.text ?? d.chunk ?? '';
      } else if (t === 'progress') {
        const text = d.message ?? d.text ?? d.finding ?? d.step_name;
        if (text) progressRef.current.push({ kind: 'finding', text });
      } else if (t === 'plan_critique') {
        // Soft-signal: surface to user for 0.5 ≤ score < 0.9.
        // Silent for score ≥ 0.9 (good plan, don't nag).
        // Auto-regenerated path already emitted critique_regenerate.
        const score = typeof d.score === 'number' ? d.score : null;
        if (score !== null && score < 0.9) {
          setCritique({
            score,
            issues: Array.isArray(d.issues) ? d.issues : [],
            hint: d.hint || '',
            comment: d.comment || '',
          });
        } else {
          setCritique(null);
        }
      } else if (t === 'critique_regenerate') {
        progressRef.current.push({
          kind: 'finding',
          text: `Plan score too low (${(d.score ?? 0).toFixed(2)}) — regenerating…`,
        });
      } else if (t === 'plan_ready' || t === 'skill_paused') {
        planReadyEvent = true;
        planToolRanRef.current = true;
        // Tree is now emitted inline in the plan_ready payload by the graph.
        if (d.tree) {
          setPendingPlan(d.tree);
          setPhase('ready');
          stopWatchdog();
          stopActivityWatchdog();
        }
        // Capture metadata for scope-aware plan summary.
        if (d.strategy || d.source_folder || d.folder_count != null) {
          setPlanMeta({
            strategy: d.strategy || null,
            source_folder: d.source_folder || null,
            folder_count: d.folder_count ?? null,
            total_documents: d.total_documents ?? null,
            files_left_in_place: d.files_left_in_place ?? 0,
            hand_placed_left: d.hand_placed_left ?? 0,
            moving: d.moving ?? null,
            staying: d.staying ?? null,
            folders_with_rules: d.folders_with_rules ?? 0,
            unsure: d.unsure ?? 0,
            unsure_in: d.unsure_in || null,
          });
        }
      } else if (t === 'apply_complete') {
        applyFinished = true;
        applyToolRanRef.current = true;
      } else if (t === 'apply_verified') {
        // State-grounded post-apply check from the skill executor. Surface a
        // non-fatal warning if requested moves don't match what landed.
        const requested = Number(d.requested_moves || 0);
        const unfiled = Number(d.actual_unfiled || 0);
        const totalDocs = Number(d.actual_total_documents || 0);
        if (requested > 0 && unfiled > 0) {
          setVerification({
            requested,
            unfiled,
            totalDocs,
            folderCount: Number(d.actual_folder_count || 0),
          });
        } else {
          setVerification(null);
        }
      } else if (t === 'termination_check') {
        // Chat-path agent violated the DONE/BLOCKED/BUDGET contract.
        // Non-fatal — the apply still happens via the explicit Apply button —
        // but we surface it so the user knows the agent's prose is unreliable.
        setTerminationWarning({
          status: d.status,
          forbidden: Array.isArray(d.forbidden) ? d.forbidden : [],
        });
      } else if (t === 'skill_finished') {
        turnDone = true;
      } else if (t === 'done') {
        turnDone = true;
      } else if (t === 'error' || t === 'skill_error' || t === 'tool_error') {
        foundError = d.error ?? d.message ?? 'Agent error';
      }
    }

    setProgressSteps([...progressRef.current]);
    setStreamText(streamTextRef.current);

    if (foundError) {
      setError(foundError);
      setPhase('error');
      stopWatchdog();
      stopActivityWatchdog();
      return;
    }
    if (applyFinished && phaseRef.current === 'applying') {
      stopWatchdog();
      stopActivityWatchdog();
      setPendingPlan(null);
      setPhase('done');
      onApplied?.();
      return;
    }
    // Plan-producing tool errored (e.g. redistribute BLOCKED) AND the agent
    // has finished its turn — show the BLOCKED message and stop the spinner.
    // The agent's final answer (with the tool's hint) is in streamText.
    if (turnDone && planToolErroredRef.current && phaseRef.current === 'running') {
      stopWatchdog();
      stopActivityWatchdog();
      setPhase('blocked');
      return;
    }

    // If done arrived without a terminal tool in any poll batch this turn,
    // surface an error. We use refs (not local vars) to survive multi-batch turns.
    if (turnDone && !planToolRanRef.current && !applyToolRanRef.current) {
      const p = phaseRef.current;
      if (p === 'running') {
        setError('Arc responded but did not generate a plan. Check the conversation and try again.');
        setPhase('error');
        stopWatchdog();
        stopActivityWatchdog();
        return;
      }
      if (p === 'applying') {
        // The agent's "done" event arrived without an "apply_complete" tool
        // result in the same poll batch.  Don't trust event ordering — verify
        // the apply happened by comparing the folder tree to the snapshot we
        // took right before /folder-apply was called.
        const startSig = applyStartSignatureRef.current;
        api.get(`/documents/case/${caseId}/folder-tree`).then(data => {
          const nowSig = treeSignature(data);
          const treeChanged = startSig !== null && nowSig !== startSig;
          if (treeChanged) {
            // State confirms the apply ran — call it success.
            stopWatchdog();
            stopActivityWatchdog();
            setPendingPlan(null);
            setPhase('done');
            onApplied?.();
          } else {
            setError('Arc responded but did not apply the plan. Check the conversation and try again.');
            setPhase('error');
            stopWatchdog();
            stopActivityWatchdog();
          }
        }).catch(() => {
          setError('Arc responded but did not apply the plan. Check the conversation and try again.');
          setPhase('error');
          stopWatchdog();
          stopActivityWatchdog();
        });
        return;
      }
    }
  }, [caseId, onApplied, stopWatchdog, stopActivityWatchdog]);

  const { start: startEvents, stop: stopEvents, getSeq } = useChatEvents(
    caseId, conversationId,
    { getToken: () => token, onEvents: handleEvents, intervalMs: 750 }
  );

  // Auto-scroll the progress feed
  useEffect(() => {
    progressEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [progressSteps, streamText]);

  // Cleanup
  useEffect(() => () => { stopEvents(); stopWatchdog(); stopActivityWatchdog(); }, [stopEvents, stopWatchdog, stopActivityWatchdog]);

  // ── Setup helpers ──
  const currentFolders = useMemo(
    () => flattenTree(currentTree?.tree || [])
      .map(n => (documentCounts ? { ...n, doc_count: documentCounts.get(n.path) || 0 } : n))
      .filter(n => n.doc_count > 0),
    [currentTree, documentCounts]
  );

  // Phase 3.3: when the case is already meaningfully organized (≥20 docs
  // in non-trivial folders), default the scope to "Unfiled only" so a
  // re-run doesn't destroy prior work. Gated on lawyerFirst — without the
  // flag the legacy "All documents" default stands. Fires once, after
  // currentTree arrives; the user can still pick "All documents" manually.
  const ORGANIZED_THRESHOLD = 20;
  const caseIsOrganized = useMemo(() => {
    const filed = currentFolders.reduce((s, f) => s + (f.doc_count || 0), 0);
    return filed >= ORGANIZED_THRESHOLD;
  }, [currentFolders]);
  const autoScopeAppliedRef = useRef(false);
  useEffect(() => {
    if (autoScopeAppliedRef.current) return;
    // Wait for currentTree to actually load before deciding.
    if (!currentTree) return;
    autoScopeAppliedRef.current = true;
    if (caseIsOrganized) {
      setScope('unfiled');
    }
  }, [currentTree, caseIsOrganized]);

  // Documents a plan leaves in a folder: second appearances always, hand-filed ones unless asked.
  const documentsThatStay = useMemo(() => {
    const out = new Map(copies || []);
    if (!includeHandPlaced) for (const [path, n] of (handPlaced || [])) out.set(path, (out.get(path) || 0) + n);
    // documents whose kind Arc could not tell are not in the plan: they stay in their folder
    for (const [path, n] of Object.entries(planMeta?.unsure_in || {})) out.set(path, (out.get(path) || 0) + n);
    return out;
  }, [copies, handPlaced, includeHandPlaced, planMeta]);

  const emptiedFolders = useMemo(() => {
    if (!pendingPlan || scope === 'unfiled') return [];
    const proposedPaths = new Set(
      flattenTree(pendingPlan.tree || []).map(n => n.path)
    );
    return currentFolders.filter(n => !proposedPaths.has(n.path)
      && !(filesThatStay?.get(n.path) > 0) && !(documentsThatStay.get(n.path) > 0));
  }, [pendingPlan, currentFolders, scope, filesThatStay, documentsThatStay]);

  // Pass 1: detect "no-op plan" — proposed counts/paths exactly match current.
  // Combined with classifySummary.unplaced > 0, this is the signal to render
  // the "no automatic placements" callout instead of an empty diff.
  const planIsNoOp = useMemo(() => {
    if (!pendingPlan || !currentTree) return false;
    return treeSignature(pendingPlan) === treeSignature(currentTree);
  }, [pendingPlan, currentTree]);

  function toggleExcludeFolder(path) {
    setExcludeFolders(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  function resetForNewTurn() {
    progressRef.current = [];
    streamTextRef.current = '';
    planToolRanRef.current = false;
    applyToolRanRef.current = false;
    planToolErroredRef.current = false;
    setProgressSteps([]);
    setStreamText('');
    setError(null);
    setCritique(null);
    setVerification(null);
    setTerminationWarning(null);
    setClassifySummary(null);
    setMergeSuggestion(null);
    setPlanError(null);
    setHeartbeatStatus(null);
    setActivityStalled(false);
    setPlanMeta(null);
  }

  // ── Action handlers ──
  // The unified agent surface: every action either starts a new graph run
  // (/agent/run) or resumes a paused one (/agent/resume).  Approve/cancel/
  // refinement are all just user_input strings the graph routes on.
  function surfaceId() {
    return scope === 'split' ? 'drawer.split' : 'drawer.reorganize';
  }

  function buildScope() {
    const s = {
      strategy,
      scope: scope === 'split' ? 'redistribute' : scope,
      exclude_folders: scope === 'exclude' ? [...excludeFolders] : [],
      delete_empty_folders: deleteEmptyFolders,
      include_hand_placed: includeHandPlaced,
    };
    if (scope === 'split' && sourceFolder) {
      s.source_folder = sourceFolder;
      s.split_mode = true;
    }
    if (mode === 'custom' && customInstructions.trim()) {
      s.custom_instructions = customInstructions.trim();
    }
    return s;
  }

  /**
   * Build the scope query string for the deterministic /preview + /apply-direct
   * endpoints. Mirrors buildScope() for the no-LLM path: split passes the chosen
   * folder, exclude passes the keep-as-is folders (repeated params, since the
   * backend reads `exclude_folders` as a list). Without these the backend's
   * `_filter_docs_to_scope` falls through to ALL docs — the split-folder
   * "21 folders for 394 documents" bug.
   */
  function buildScopeQuery(wireScope, moveHandPlaced = includeHandPlaced) {
    const qs = new URLSearchParams();
    qs.set('scheme', strategy);
    qs.set('scope', wireScope);
    if (moveHandPlaced) qs.set('include_hand_placed', 'true');
    if (scope === 'split' && sourceFolder) qs.set('source_folder', sourceFolder);
    if (scope === 'exclude') {
      [...excludeFolders].forEach(f => qs.append('exclude_folders', f));
    }
    return qs.toString();
  }

  async function handleStart() {
    resetForNewTurn();
    setPendingPlan(null);
    seenSeqsRef.current = new Set();
    setPreviewData(null);
    setPhase('starting');
    try {
      const result = await api.post('/agent/run', {
        surface_id: surfaceId(),
        case_id: caseId,
        scope: buildScope(),
      });
      conversationIdRef.current = result.conversation_id;
      setConversationId(result.conversation_id);
      setPhase('running');
      startEvents(0, result.conversation_id);
      startWatchdog(PLAN_TIMEOUT_MS, 'Arc took too long to generate a plan. Please try again.');
      startActivityWatchdog();
    } catch (err) {
      setError('Failed to start: ' + err.message);
      setPhase('error');
    }
  }

  /**
   * Phase 3.2: dry-run preview path. Fetches /preview (deterministic, no LLM)
   * and parks on a preview screen with two action buttons:
   *   - Apply directly      → /apply-direct (still no LLM)
   *   - Arc refine + name   → handleStart (existing LLM propose path)
   * Only the deterministic schemes (by_role / by_entity / by_date) reach
   * this code path; by_source is disabled and auto/by_subject/by_type fall
   * through to the legacy dropdown which doesn't gate on preview.
   */
  async function handlePreview(moveHandPlaced = includeHandPlaced) {
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const wireScope = scope === 'split' ? 'redistribute' : scope;
      const data = await api.get(
        `/documents/case/${caseId}/reorganize/preview?${buildScopeQuery(wireScope, moveHandPlaced)}`,
      );
      setPreviewData(data);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleApplyDirect() {
    if (!previewData) return;
    setPhase('applying');
    try {
      const wireScope = scope === 'split' ? 'redistribute' : scope;
      const result = await api.post(
        `/documents/case/${caseId}/reorganize/apply-direct`
          + `?${buildScopeQuery(wireScope)}`
          + `&delete_empty_folders=${deleteEmptyFolders ? 'true' : 'false'}`,
        null,
      );
      // Backend can return 200 with moved_count=0 + errors[] (e.g. when
      // plan paths fail `normalize_folder_path` server-side). Treat that
      // as a visible failure instead of silently closing the drawer.
      const moved = result?.moved_count ?? 0;
      const errs = result?.errors ?? [];
      if (errs.length > 0) {
        setError(
          `Apply moved ${moved} doc${moved === 1 ? '' : 's'} — ${errs.length} error${errs.length === 1 ? '' : 's'}: ${errs[0]}`
        );
        setPhase('error');
        return;
      }
      // Nothing to move because everything is already in its folder is not a failure:
      // the folders still learn what belongs in them (plan §7e).
      const nothingToMove = previewData.moving === 0 && (previewData.staying ?? 0) > 0;
      if (moved === 0 && !nothingToMove) {
        setError(
          `Apply returned 0 documents moved across ${result?.folder_count ?? 0} folders. The plan didn't match any documents — try a different scope or scheme.`
        );
        setPhase('error');
        return;
      }
      setPreviewData(null);
      setPhase('done');
      if (typeof onApplied === 'function') onApplied();
    } catch (err) {
      setError('Apply failed: ' + err.message);
      setPhase('error');
    }
  }

  async function handleApply() {
    if (!conversationId || !pendingPlan) return;
    resetForNewTurn();
    setPhase('applying');
    // Snapshot the folder tree before the apply turn so we can verify the
    // outcome from state if the apply_complete event is lost / out of order.
    try {
      const preApply = await api.get(`/documents/case/${caseId}/folder-tree`);
      applyStartSignatureRef.current = treeSignature(preApply);
    } catch {
      applyStartSignatureRef.current = null;
    }
    try {
      await api.post('/agent/resume', {
        conversation_id: conversationId,
        case_id: caseId,
        user_input: 'approve',
      });
      // Resume polling from current seq — the apply turn appends new events
      startEvents(getSeq(), conversationId);
      startWatchdog(APPLY_TIMEOUT_MS, 'Arc took too long to apply. Refresh to see current state.');
      startActivityWatchdog();
    } catch (err) {
      setError('Failed to apply: ' + err.message);
      setPhase('ready');
    }
  }

  async function handleIterate() {
    if (!refinement.trim() || !conversationId) return;
    const msg = refinement.trim();
    setRefinement('');
    setIterating(true);
    resetForNewTurn();
    setPendingPlan(null);
    setPhase('running');
    try {
      await api.post('/agent/resume', {
        conversation_id: conversationId,
        case_id: caseId,
        user_input: msg,
      });
      startEvents(getSeq(), conversationId);
      startWatchdog(PLAN_TIMEOUT_MS, 'Arc took too long to refine. Please try again.');
      startActivityWatchdog();
    } catch (err) {
      setError('Refinement failed: ' + err.message);
      setPhase('ready');
    } finally {
      setIterating(false);
    }
  }

  async function handlePostApplyRefine() {
    // Post-apply refinement: the prior graph completed, so start a fresh run.
    if (!refinement.trim()) return;
    const msg = refinement.trim();
    setRefinement('');
    setIterating(true);
    resetForNewTurn();
    setPendingPlan(null);
    seenSeqsRef.current = new Set();
    setPhase('starting');
    try {
      const result = await api.post('/agent/run', {
        surface_id: 'drawer.reorganize',
        case_id: caseId,
        scope: { ...buildScope(), custom_instructions: msg },
      });
      conversationIdRef.current = result.conversation_id;
      setConversationId(result.conversation_id);
      setPhase('running');
      startEvents(0, result.conversation_id);
      startWatchdog(PLAN_TIMEOUT_MS, 'Arc took too long to refine. Please try again.');
      startActivityWatchdog();
    } catch (err) {
      setError('Refinement failed: ' + err.message);
      setPhase('done');
    } finally {
      setIterating(false);
    }
  }

  async function handleTryDifferent() {
    resetForNewTurn();
    setPendingPlan(null);
    seenSeqsRef.current = new Set();
    setPhase('starting');
    try {
      const result = await api.post('/agent/run', {
        surface_id: surfaceId(),
        case_id: caseId,
        scope: { ...buildScope(), strategy: 'auto' },
      });
      conversationIdRef.current = result.conversation_id;
      setConversationId(result.conversation_id);
      setPhase('running');
      startEvents(0, result.conversation_id);
      startWatchdog(PLAN_TIMEOUT_MS, 'Arc took too long. Please try again.');
    } catch (err) {
      setError('Failed: ' + err.message);
      setPhase('error');
    }
  }

  function handleRefinementKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (phase === 'done') handlePostApplyRefine();
      else handleIterate();
    }
  }

  // ── Live chatter feed ──
  function renderChatter() {
    if (!progressSteps.length && !streamText) {
      return <p className="reorg-hint">Arc is starting…</p>;
    }
    return (
      <div className="reorg-chatter">
        <div className="stream-progress reorg-progress">
          {progressSteps.map((step, i) => (
            <div key={i} className={`progress-step progress-${step.kind}`}>
              {step.kind === 'done' ? '✓ ' : step.kind === 'finding' ? '· ' : '› '}
              {step.text}
            </div>
          ))}
          <div ref={progressEndRef} />
        </div>
        {streamText && <div className="reorg-stream-text">{streamText}</div>}
      </div>
    );
  }

  return (
    <div className="reorg-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="reorg-drawer">
        {/* Header */}
        <div className="reorg-header">
          <span className="reorg-title">Reorganize with Arc</span>
          <button className="btn btn-ghost btn-sm reorg-close" onClick={onClose}>✕</button>
        </div>

        {/* Idle: setup */}
        {phase === 'idle' && (
          <div className="reorg-body reorg-setup">
            <p className="reorg-desc">Arc will analyze your documents and propose a folder structure.</p>

            {/* Mode toggle */}
            <div className="reorg-mode-toggle" style={{
              display: 'flex', gap: 8, marginBottom: 12,
              padding: 4, background: '#f1f3f5', borderRadius: 8,
            }}>
              <button
                type="button"
                onClick={() => setMode('standard')}
                className="btn btn-sm"
                style={{
                  flex: 1,
                  background: mode === 'standard' ? '#ffffff' : 'transparent',
                  boxShadow: mode === 'standard' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  fontWeight: mode === 'standard' ? 600 : 400,
                }}
              >
                Standard
              </button>
              <button
                type="button"
                onClick={() => setMode('custom')}
                className="btn btn-sm"
                style={{
                  flex: 1,
                  background: mode === 'custom' ? '#ffffff' : 'transparent',
                  boxShadow: mode === 'custom' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  fontWeight: mode === 'custom' ? 600 : 400,
                }}
              >
                Custom instructions
              </button>
            </div>

            {mode === 'custom' ? (
              <>
                <label className="reorg-label">
                  What should Arc do?
                  <textarea
                    className="reorg-refine-input"
                    rows={5}
                    placeholder={'e.g. "Most files in the Other folder are photos. Move them to the Photo folder and delete Other." or "Merge Letters and Affidavits into Correspondence, then split Police Reports by year."'}
                    value={customInstructions}
                    onChange={e => setCustomInstructions(e.target.value)}
                    style={{ width: '100%', marginTop: 6, padding: 8, fontSize: 13 }}
                  />
                </label>
                <p className="reorg-hint" style={{ marginTop: 4 }}>
                  Arc will propose the changes first — you'll review and approve before anything moves.
                </p>
                <button
                  className="btn btn-primary reorg-start-btn"
                  onClick={handleStart}
                  disabled={!customInstructions.trim()}
                >
                  Propose plan
                </button>
              </>
            ) : (
              <>
                <SchemePicker
                  caseId={caseId}
                  scope={scope}
                  sourceFolder={sourceFolder}
                  excludeFolders={[...excludeFolders]}
                  selected={strategy}
                  onPick={setStrategy}
                />

                <div className="reorg-label">Scope</div>
                {caseIsOrganized && (
                  <div style={{
                    fontSize: 12, color: '#5a4b1f',
                    background: '#fdf6dd', border: '1px solid #f0d97e',
                    padding: '8px 10px', borderRadius: 4,
                    marginBottom: 8,
                  }}>
                    <strong>This case already has organized folders.</strong> Arc
                    will only touch unfiled documents by default — pick
                    "All documents" if you want to replace the existing
                    structure.
                  </div>
                )}
                <div className="reorg-scope-group">
                  <label className={`reorg-scope-option ${scope === 'all' ? 'reorg-scope-selected' : ''}`}>
                    <input type="radio" name="scope" value="all" checked={scope === 'all'} onChange={() => setScope('all')} />
                    <div>
                      <strong>All documents</strong>
                      <p>Reorganize everything — existing folders are replaced with the new structure.</p>
                    </div>
                  </label>
                  <label className={`reorg-scope-option ${scope === 'unfiled' ? 'reorg-scope-selected' : ''}`}>
                    <input type="radio" name="scope" value="unfiled" checked={scope === 'unfiled'} onChange={() => setScope('unfiled')} />
                    <div>
                      <strong>Unfiled only</strong>
                      <p>Only organize documents not yet in any folder. Existing folders are untouched.</p>
                    </div>
                  </label>
                  {currentFolders.length > 0 && (
                    <label className={`reorg-scope-option ${scope === 'exclude' ? 'reorg-scope-selected' : ''}`}>
                      <input type="radio" name="scope" value="exclude" checked={scope === 'exclude'} onChange={() => setScope('exclude')} />
                      <div>
                        <strong>All except…</strong>
                        <p>Reorganize everything but keep selected folders as-is.</p>
                      </div>
                    </label>
                  )}
                  {currentFolders.length > 0 && (
                    <label className={`reorg-scope-option ${scope === 'split' ? 'reorg-scope-selected' : ''}`}>
                      <input type="radio" name="scope" value="split" checked={scope === 'split'} onChange={() => setScope('split')} />
                      <div>
                        <strong>✦ Split a folder</strong>
                        <p>Break one large folder into targeted subfolders.</p>
                      </div>
                    </label>
                  )}
                </div>

                {scope === 'split' && (
                  <div style={{ marginTop: 8 }}>
                    <select
                      className="reorg-select"
                      value={sourceFolder}
                      onChange={e => setSourceFolder(e.target.value)}
                    >
                      <option value="">Choose a folder…</option>
                      {currentFolders.map(f => (
                        <option key={f.path} value={f.path}>
                          {f.name} ({f.doc_count} doc{f.doc_count !== 1 ? 's' : ''})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {scope === 'exclude' && currentFolders.length > 0 && (
                  <div className="reorg-exclude-list">
                    <div className="reorg-exclude-label">Keep these folders unchanged:</div>
                    {currentFolders.map(folder => (
                      <label key={folder.path} className="reorg-exclude-item">
                        <input
                          type="checkbox"
                          checked={excludeFolders.has(folder.path)}
                          onChange={() => toggleExcludeFolder(folder.path)}
                        />
                        <span className="reorg-exclude-name">{folder.name}</span>
                        <span className="reorg-exclude-count">{folder.doc_count}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Deterministic preview gate before propose. When
                    previewData is set, render the preview screen with
                    Apply directly / Arc refine + name. Otherwise render
                    the Preview button which fetches the deterministic
                    grouping (no LLM) so the user sees the structure
                    before committing to an LLM call. */}
                {previewData ? (
                  <PreviewScreen
                    previewData={previewData}
                    includeHandPlaced={includeHandPlaced}
                    onToggleHandPlaced={(v) => { setIncludeHandPlaced(v); handlePreview(v); }}
                    onApplyDirect={handleApplyDirect}
                    onArcRefine={handleStart}
                    onBack={() => setPreviewData(null)}
                  />
                ) : (
                  <>
                    <button
                      className="btn btn-primary reorg-start-btn"
                      onClick={() => handlePreview()}
                      disabled={
                        previewLoading ||
                        !strategy ||
                        (scope === 'exclude' && excludeFolders.size === 0) ||
                        (scope === 'split' && !sourceFolder)
                      }
                    >
                      {previewLoading ? 'Loading preview…' : 'Preview folders'}
                    </button>
                    {previewError && (
                      <p className="reorg-hint" style={{ color: '#c33' }}>
                        Couldn't load preview: {previewError}
                      </p>
                    )}
                  </>
                )}
                {scope === 'exclude' && excludeFolders.size === 0 && (
                  <p className="reorg-hint">Select at least one folder to exclude.</p>
                )}
                {scope === 'split' && !sourceFolder && (
                  <p className="reorg-hint">Select a folder to split.</p>
                )}
              </>
            )}
          </div>
        )}

        {/* Running — show live chatter */}
        {(phase === 'starting' || phase === 'running') && (
          <div className="reorg-body reorg-waiting">
            <div className="reorg-waiting-header">
              {orbVisible && <ArcOrb size={36} state={orbState} onDone={hideOrb} />}
              <p>
                {heartbeatStatus
                  ? `${toolLabel(heartbeatStatus.tool)}… ${Math.round(heartbeatStatus.elapsed_ms / 1000)}s`
                  : 'Arc is working…'}
              </p>
            </div>
            {activityStalled && (
              <div style={{
                margin: '8px 0',
                padding: '8px 12px',
                background: '#f5f7fa',
                border: '1px solid #d7dde4',
                borderRadius: 6,
                fontSize: 13,
                color: '#555',
              }}>
                Arc is still working — this step is taking longer than usual.
              </div>
            )}
            {renderChatter()}
          </div>
        )}

        {/* Blocked — tool returned an error (e.g. redistribute couldn't
            confidently place any docs). Show the agent's BLOCKED message
            (which includes the next-step hint) and let the user retry. */}
        {phase === 'blocked' && (
          <div className="reorg-body">
            {renderChatter()}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-sm" onClick={() => setPhase('idle')}>
                Try a different approach
              </button>
              <button className="btn btn-sm btn-ghost" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        )}

        {/* Plan ready */}
        {(phase === 'ready' || phase === 'applying') && pendingPlan && (
          <>
            <div className="reorg-plan-summary">
              {scope === 'split' ? (
                <span>
                  Splitting <strong>{sourceFolder}</strong> into{' '}
                  <strong>
                    {planMeta?.folder_count ?? pendingPlan.tree?.length ?? '?'}{' '}
                    subfolder{(planMeta?.folder_count ?? pendingPlan.tree?.length ?? 0) !== 1 ? 's' : ''}
                  </strong>
                  {planMeta?.total_documents != null && (
                    <> for <strong>{planMeta.total_documents} doc{planMeta.total_documents !== 1 ? 's' : ''}</strong></>
                  )}
                </span>
              ) : planMeta?.strategy === 'redistribute' ? (
                <span>
                  Moving <strong>{planMeta.total_documents ?? '?'} doc{planMeta.total_documents !== 1 ? 's' : ''}</strong>
                  {' '}from <strong>{planMeta.source_folder}</strong>
                  {' '}into <strong>{planMeta.folder_count ?? (pendingPlan.tree?.length || 0)} existing folder{planMeta.folder_count !== 1 ? 's' : ''}</strong>
                  <span style={{ marginLeft: 8, opacity: 0.55, fontWeight: 400, fontSize: 12 }}>
                    — other folders unchanged
                  </span>
                </span>
              ) : (
                <span>
                  Proposed: <strong>{pendingPlan.tree?.length || 0} folder{pendingPlan.tree?.length !== 1 ? 's' : ''}</strong>
                  {' '}for <strong>{pendingPlan.total_count} doc{pendingPlan.total_count !== 1 ? 's' : ''}</strong>
                </span>
              )}
            </div>
            <WhatChanges moving={planMeta?.moving} staying={planMeta?.staying} inset />
            <FilesLeftInPlace count={planMeta?.files_left_in_place} inset />
            <HandPlacedLeft count={planMeta?.hand_placed_left} inset />
            <SelfFiling folders={planMeta?.folders_with_rules} unsure={planMeta?.unsure} inset />

            {critique && (
              <div className="reorg-critique-notice" style={{
                margin: '8px 0',
                padding: '10px 12px',
                background: critique.score < 0.7 ? '#fff4e6' : '#f5f7fa',
                border: `1px solid ${critique.score < 0.7 ? '#f5b971' : '#d7dde4'}`,
                borderRadius: 6,
                fontSize: 13,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <strong>Plan review</strong>
                  <span style={{ opacity: 0.75 }}>score {critique.score.toFixed(2)}</span>
                </div>
                {critique.comment && <div style={{ marginBottom: 4 }}>{critique.comment}</div>}
                {critique.issues?.length > 0 && (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                    {critique.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                  </ul>
                )}
                {critique.hint && (
                  <div style={{ marginTop: 6, fontStyle: 'italic', opacity: 0.8 }}>
                    Suggestion: {critique.hint}
                  </div>
                )}
              </div>
            )}

            {terminationWarning && (
              <div style={{
                margin: '8px 0',
                padding: '10px 12px',
                background: '#fff4e6',
                border: '1px solid #f5b971',
                borderRadius: 6,
                fontSize: 13,
              }}>
                <strong>Agent didn't end cleanly</strong>
                <div style={{ marginTop: 4, opacity: 0.85 }}>
                  {terminationWarning.status === 'forbidden_phrase'
                    ? `The agent promised future work without acting on it (${terminationWarning.forbidden.slice(0, 2).join(', ')}). Trust the plan above, not the agent's prose.`
                    : 'The agent didn\'t emit a DONE/BLOCKED/BUDGET marker. Verify the plan looks right before applying.'}
                </div>
              </div>
            )}

            {mergeSuggestion && (
              <div style={{
                margin: '8px 0',
                padding: '10px 12px',
                background: '#eef6ff',
                border: '1px solid #b8d6fa',
                borderRadius: 6,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                justifyContent: 'space-between',
              }}>
                <div>
                  <strong>{mergeSuggestion.from}</strong> looks like a duplicate of{' '}
                  <strong>{mergeSuggestion.into}</strong>.
                  {mergeSuggestion.reason && (
                    <div style={{ marginTop: 2, opacity: 0.75, fontSize: 12 }}>
                      {mergeSuggestion.reason}
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-sm"
                  disabled={iterating || phase === 'applying'}
                  onClick={() => {
                    setRefinement(
                      `Merge ${mergeSuggestion.from} into ${mergeSuggestion.into} ` +
                      `using refine_folder_plan, then end with DONE.`
                    );
                    setMergeSuggestion(null);
                    setTimeout(() => handleIterate(), 0);
                  }}
                >
                  Merge them
                </button>
              </div>
            )}

            {planIsNoOp && classifySummary && classifySummary.unplaced > 0 && (
              <div style={{
                margin: '8px 0',
                padding: '12px 14px',
                background: '#fbfbfb',
                border: '1px solid #e1e4e8',
                borderRadius: 6,
                fontSize: 13,
              }}>
                <strong>No automatic placements found.</strong>
                <p style={{ margin: '6px 0 8px' }}>
                  These {classifySummary.unplaced} document{classifySummary.unplaced !== 1 ? 's' : ''} don't have distinctive
                  enough metadata for Arc to confidently slot them into existing folders.
                </p>
                {classifySummary.decisions?.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer' }}>Why? (per-document reasons)</summary>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12 }}>
                      {classifySummary.decisions.slice(0, 8).map(d => (
                        <li key={d.id} style={{ marginBottom: 4 }}>
                          <code>{d.filename || d.id}</code>
                          {' — '}
                          <span style={{ opacity: 0.85 }}>
                            {d.reason || 'no clear category'}
                          </span>
                        </li>
                      ))}
                      {classifySummary.decisions.length > 8 && (
                        <li style={{ opacity: 0.6 }}>
                          …and {classifySummary.decisions.length - 8} more
                        </li>
                      )}
                    </ul>
                  </details>
                )}
                <p style={{ margin: '8px 0 0', opacity: 0.75, fontSize: 12 }}>
                  You can leave these as-is, move them manually from the documents list,
                  or close this drawer.
                </p>
              </div>
            )}

            <div className="reorg-diff-area">
              <FolderTreeDiff currentTree={currentTree} proposedTree={pendingPlan} scope={scope}
                documentCounts={documentCounts} filesThatStay={filesThatStay} documentsThatStay={documentsThatStay} />
            </div>

            {emptiedFolders.length > 0 && (
              <div className="reorg-empty-notice">
                <strong>{emptiedFolders.length} folder{emptiedFolders.length !== 1 ? 's' : ''} will be emptied</strong>
                <div className="reorg-empty-names">
                  {emptiedFolders.map(f => f.name).join(' · ')}
                </div>
                <label className="reorg-check">
                  <input
                    type="checkbox"
                    checked={deleteEmptyFolders}
                    onChange={e => setDeleteEmptyFolders(e.target.checked)}
                    disabled={phase === 'applying'}
                  />
                  Delete these after applying
                </label>
              </div>
            )}

            <div className="reorg-refine-area">
              <textarea
                ref={refinementRef}
                className="reorg-refine-input"
                placeholder='Refine the plan, e.g. "merge Letters and Affidavits" or "split Police Reports by year"'
                value={refinement}
                onChange={e => setRefinement(e.target.value)}
                onKeyDown={handleRefinementKey}
                rows={2}
                disabled={iterating || phase === 'applying'}
              />
              <button
                className="btn btn-sm"
                onClick={handleIterate}
                disabled={!refinement.trim() || iterating || phase === 'applying'}
              >
                {iterating ? 'Refining…' : 'Refine'}
              </button>
            </div>
          </>
        )}

        {/* Applying — overlay with live chatter */}
        {phase === 'applying' && (
          <div className="reorg-applying-overlay">
            {orbVisible && <ArcOrb size={48} state={orbState} onDone={hideOrb} />}
            <p>Arc is applying the plan…</p>
            {renderChatter()}
          </div>
        )}

        {/* Done — feedback prompt + iteration */}
        {phase === 'done' && (
          <div className="reorg-body reorg-done">
            <p className="reorg-done-msg">{scope === 'split' ? '✓ Folder split.' : '✓ Folders reorganized.'}</p>
            <SelfFiling folders={selfFiling} done />
            {verification && (
              <div style={{
                margin: '8px 0',
                padding: '10px 12px',
                background: '#fff4e6',
                border: '1px solid #f5b971',
                borderRadius: 6,
                fontSize: 13,
              }}>
                <strong>Verification: {verification.unfiled} doc{verification.unfiled !== 1 ? 's' : ''} still unfiled</strong>
                <div style={{ marginTop: 4, opacity: 0.85 }}>
                  Apply requested {verification.requested} move{verification.requested !== 1 ? 's' : ''}, but {verification.unfiled} document{verification.unfiled !== 1 ? 's' : ''} remain in /other or unfiled. Run reorganize again or refine the plan to cover them.
                </div>
              </div>
            )}
            {renderChatter()}
            <p className="reorg-desc">
              How does it look? You can ask Arc to adjust further — for example,
              "merge X and Y", "split Z by date", or "move all medical records together".
            </p>
            <div className="reorg-refine-area">
              <textarea
                ref={refinementRef}
                className="reorg-refine-input"
                placeholder='Want any adjustments? Type here, or click "Looks good".'
                value={refinement}
                onChange={e => setRefinement(e.target.value)}
                onKeyDown={handleRefinementKey}
                rows={2}
                disabled={iterating}
              />
              <button
                className="btn btn-sm"
                onClick={handlePostApplyRefine}
                disabled={!refinement.trim() || iterating}
              >
                {iterating ? 'Refining…' : 'Refine further'}
              </button>
            </div>
            <button className="btn btn-primary" onClick={onClose}>Looks good — close</button>
          </div>
        )}

        {/* Structured plan_error / plan_budget_exceeded banner */}
        {phase === 'error' && planError && (
          <div className="reorg-body" style={{ paddingTop: 0 }}>
            <div style={{
              padding: '12px 14px',
              background: '#fff4e6',
              border: '1px solid #f5b971',
              borderRadius: 6,
              fontSize: 13,
            }}>
              <strong>Arc couldn't complete this step</strong>
              {planError.tool && (
                <span style={{ marginLeft: 8, opacity: 0.65, fontWeight: 400 }}>
                  ({planError.tool.replace(/_/g, ' ')})
                </span>
              )}
              <div style={{ marginTop: 6 }}>{planError.message}</div>
              {planError.hint && (
                <div style={{ marginTop: 4, fontStyle: 'italic', opacity: 0.8 }}>
                  {planError.hint}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {planError.recoverable !== false && (
                <button className="btn btn-sm btn-primary" onClick={handleStart}>
                  Retry
                </button>
              )}
              <button className="btn btn-sm btn-ghost" onClick={() => { setPlanError(null); setPhase('idle'); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Legacy string error (non-plan_error paths) */}
        {error && !planError && <div className="reorg-error">{error}</div>}

        {/* Footer */}
        {(phase === 'ready' || phase === 'applying') && (
          <div className="reorg-footer">
            <button className="btn btn-primary" onClick={handleApply} disabled={phase === 'applying'}>
              {phase === 'applying' ? 'Applying…' : scope === 'split' ? 'Apply split' : 'Apply this plan'}
            </button>
            <button className="btn btn-ghost" onClick={handleTryDifferent} disabled={phase === 'applying'}>
              Try different approach
            </button>
            <button className="btn btn-ghost" onClick={onClose} disabled={phase === 'applying'}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * Phase 3.2: deterministic-preview screen rendered between scheme pick
 * and propose when the lawyer-first flag is on. Two action paths:
 *   - "Apply directly"        → /apply-direct (no LLM, instant)
 *   - "Have Arc refine + name" → existing propose+critique+apply flow
 * "Back to picker" clears previewData so the user can pick a different
 * scheme or scope without losing form state.
 */
// A file that was split into documents is not filed again: it stays where it was put
// and its documents are filed one by one.
function FilesLeftInPlace({ count, inset = false }) {
  if (!count) return null;
  return (
    <div data-testid="reorg-files-left" style={{ fontSize: 12, color: '#445', marginTop: 8, padding: inset ? '0 16px 6px' : 0 }}>
      {count === 1
        ? '1 file that holds several documents stays where it is — its documents are filed one by one.'
        : `${count} files that hold several documents stay where they are — their documents are filed one by one.`}
    </div>
  );
}

// The folders of an applied plan know what belongs in them: new documents file themselves.
function SelfFiling({ folders, unsure = 0, done = false, inset = false }) {
  if (!folders && !unsure) return null;
  return (
    <div data-testid="reorg-self-filing" style={{ fontSize: 12, color: '#445', marginTop: 8, padding: inset ? '0 16px 6px' : 0 }}>
      {folders > 0 && (
        <>
          {done ? 'From now on, a ' : 'Once applied, a '}new document files itself into the folder that holds its kind
          {' '}(<strong>{folders}</strong> folder{folders === 1 ? '' : 's'} marked ✦). The ones Arc is unsure about are
          listed under <strong>Check these</strong>.
        </>
      )}
      {unsure > 0 && (
        <div style={{ marginTop: folders > 0 ? 4 : 0 }}>
          {unsure === 1
            ? '1 document whose kind Arc could not tell is left where it is — find it under Check these.'
            : `${unsure} documents whose kind Arc could not tell are left where they are — find them under Check these.`}
        </div>
      )}
    </div>
  );
}

// A re-run shows what it would change: a document already in its folder is not moved.
function WhatChanges({ moving, staying, inset = false }) {
  if (moving == null || staying == null) return null;
  return (
    <div data-testid="reorg-what-changes" style={{ fontSize: 12, color: '#445', marginTop: 8, padding: inset ? '0 16px' : 0 }}>
      <strong>{moving}</strong> document{moving === 1 ? '' : 's'} would move
      {' · '}<strong>{staying}</strong> already in place
    </div>
  );
}

// What a person filed by hand is theirs: a plan leaves it alone unless they say otherwise.
function HandPlacedLeft({ count, moveThose = false, onToggle = null, inset = false }) {
  if (!count && !moveThose) return null;
  return (
    <div data-testid="reorg-hand-placed" style={{ fontSize: 12, color: '#445', marginTop: 8, padding: inset ? '0 16px 6px' : 0 }}>
      {moveThose
        ? 'The documents you filed by hand are included in this plan.'
        : (count === 1
          ? '1 document you filed by hand stays where you put it.'
          : `${count} documents you filed by hand stay where you put them.`)}
      {onToggle && (
        <label style={{ marginLeft: 8, whiteSpace: 'nowrap' }}>
          <input type="checkbox" data-testid="reorg-move-hand-placed" checked={moveThose} onChange={e => onToggle(e.target.checked)} />
          {' '}Move those too
        </label>
      )}
    </div>
  );
}

function PreviewScreen({ previewData, includeHandPlaced = false, onToggleHandPlaced = null, onApplyDirect, onArcRefine, onBack }) {
  const folders = previewData.folders || [];
  const total = folders.reduce((s, f) => s + (f.doc_count || 0), 0);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{
        background: '#fff', border: '1px solid #d0d4dc', borderRadius: 6,
        padding: 12, marginBottom: 10,
      }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
          Preview — {folders.length} folder{folders.length === 1 ? '' : 's'} for {total} document{total === 1 ? '' : 's'}
        </div>
        <ul style={{
          margin: 0, paddingLeft: 18, fontSize: 12,
          maxHeight: 260, overflowY: 'auto', lineHeight: 1.6,
        }}>
          {folders.map(f => (
            <li key={f.path}>
              <code style={{ color: '#445' }}>{f.path}</code>
              <span style={{ color: '#888', marginLeft: 6 }}>
                {f.doc_count} doc{f.doc_count === 1 ? '' : 's'}
                {f.arriving > 0 && f.arriving < f.doc_count && <> · {f.arriving} new here</>}
              </span>
            </li>
          ))}
        </ul>
        <WhatChanges moving={previewData.moving} staying={previewData.staying} />
        <FilesLeftInPlace count={previewData.files_left_in_place} />
        <HandPlacedLeft count={previewData.hand_placed_left} moveThose={includeHandPlaced} onToggle={onToggleHandPlaced} />
        <SelfFiling folders={previewData.folders_with_rules} unsure={previewData.unsure} />
        <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
          Computed deterministically from document classification — no AI call yet.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-primary" onClick={onApplyDirect}>
          Apply directly
        </button>
        <button className="btn btn-ghost" onClick={onArcRefine}>
          Have Arc refine + name
        </button>
        <button className="btn btn-ghost" onClick={onBack} style={{ marginLeft: 'auto' }}>
          Back
        </button>
      </div>
      <p className="reorg-hint" style={{ marginTop: 6 }}>
        <strong>Apply directly</strong> moves your documents into the folders above immediately.
        <br/>
        <strong>Have Arc refine + name</strong> lets the AI rename, sub-split, or adjust the structure before you approve.
      </p>
    </div>
  );
}
